import React, { useState, useEffect, useMemo } from 'react';
import { CheckCircle, XCircle, Eye, RefreshCw, Filter, Search, Calendar, User, FileText, Info, X, AlertCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import authUtils from '../utils/authUtils';

const ApprovalManagement = () => {
  // State Management
  const [requestList, setRequestList] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter states
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('ALL'); // ✅ Mặc định là ALL
  const [filterType, setFilterType] = useState('ALL');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Expanded rows for detail view
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Bulk approval states
  const [selectedRequests, setSelectedRequests] = useState(new Set());
  const [showBulkApprovalModal, setShowBulkApprovalModal] = useState(false);
  const [bulkApprovalAction, setBulkApprovalAction] = useState('');
  const [bulkApprovalNote, setBulkApprovalNote] = useState('');
  const [bulkRejectReason, setBulkRejectReason] = useState('');

  // ==================== FETCH FUNCTIONS ====================

  const fetchRequestList = async () => {
    try {
      setIsLoading(true);
      const response = await authUtils.apiRequestKHO('XUATNHAPKHO_REQUEST', 'Find', {});

      response.sort((a, b) => new Date(b['THOI_GIAN_YEU_CAU']) - new Date(a['THOI_GIAN_YEU_CAU']));

      setRequestList(response);
    } catch (error) {
      console.error('Error fetching request list:', error);
      toast.error('Lỗi khi tải danh sách yêu cầu');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCurrentUser = async () => {
    try {
      const localUser = authUtils.getUserData();
      if (localUser) {
        setCurrentUser(localUser);
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  useEffect(() => {
    fetchRequestList();
    fetchCurrentUser();
  }, []);

  // ==================== HELPER FUNCTIONS ====================

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('vi-VN');
    } catch (error) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount || amount === 0) return '0 ₫';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatNumber = (number, decimals = 4) => {
    if (!number || isNaN(number)) return '0' + ',0000'.slice(0, decimals + 1);
    return parseFloat(number).toFixed(decimals).replace('.', ',');
  };

  const getStatusBadge = (status) => {
    const badges = {
      'PENDING': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Chờ duyệt', icon: <Clock className="w-3 h-3" /> },
      'APPROVED': { bg: 'bg-green-100', text: 'text-green-800', label: 'Đã duyệt', icon: <CheckCircle className="w-3 h-3" /> },
      'REJECTED': { bg: 'bg-red-100', text: 'text-red-800', label: 'Từ chối', icon: <XCircle className="w-3 h-3" /> }
    };
    const badge = badges[status] || badges['PENDING'];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text} gap-1`}>
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const badges = {
      'ADD': { bg: 'bg-green-100', text: 'text-green-800', label: 'Thêm mới' },
      'EDIT': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sửa đổi' },
      'DELETE': { bg: 'bg-red-100', text: 'text-red-800', label: 'Xóa' }
    };
    const badge = badges[type] || badges['EDIT'];
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  // ✅ CẬP NHẬT: Hàm render field với highlight inline
  const renderFieldWithHighlight = (label, oldValue, newValue) => {
    const hasChanged = String(oldValue) !== String(newValue);

    if (!hasChanged) {
      return (
        <div className="text-xs">
          <strong>{label}:</strong> {oldValue || '-'}
        </div>
      );
    }

    return (
      <div className="text-xs">
        <strong>{label}:</strong>
        <span className="ml-1">
          <span className="line-through text-red-600 bg-red-50 px-1.5 py-0.5 rounded mr-1">
            {oldValue || '(trống)'}
          </span>
          <span className="text-gray-400 mx-1">→</span>
          <span className="text-green-600 bg-green-100 px-1.5 py-0.5 rounded font-semibold">
            {newValue || '(trống)'}
          </span>
        </span>
      </div>
    );
  };

  // ==================== FILTER FUNCTIONS ====================

  const filteredRequests = useMemo(() => {
    return requestList.filter(request => {
      const searchLower = search.toLowerCase();
      const matchesSearch = (
        request['REQUEST_ID']?.toLowerCase().includes(searchLower) ||
        request['SOPHIEU']?.toLowerCase().includes(searchLower) ||
        request['MA_KIEN_NEW']?.toLowerCase().includes(searchLower) ||
        request['MA_KIEN_OLD']?.toLowerCase().includes(searchLower) ||
        request['NGUOI_YEU_CAU']?.toLowerCase().includes(searchLower)
      );

      const matchesStatus = filterStatus === 'ALL' || request['TRANG_THAI_DUYET'] === filterStatus;
      const matchesType = filterType === 'ALL' || request['LOAI_YEU_CAU'] === filterType;

      let matchesDateRange = true;
      if (filterDateFrom || filterDateTo) {
        const requestDate = new Date(request['THOI_GIAN_YEU_CAU']);
        if (filterDateFrom) {
          const fromDate = new Date(filterDateFrom);
          matchesDateRange = matchesDateRange && requestDate >= fromDate;
        }
        if (filterDateTo) {
          const toDate = new Date(filterDateTo);
          toDate.setHours(23, 59, 59, 999);
          matchesDateRange = matchesDateRange && requestDate <= toDate;
        }
      }

      return matchesSearch && matchesStatus && matchesType && matchesDateRange;
    });
  }, [requestList, search, filterStatus, filterType, filterDateFrom, filterDateTo]);

  const statistics = useMemo(() => {
    return {
      pending: filteredRequests.filter(r => r['TRANG_THAI_DUYET'] === 'PENDING').length,
      approved: filteredRequests.filter(r => r['TRANG_THAI_DUYET'] === 'APPROVED').length,
      rejected: filteredRequests.filter(r => r['TRANG_THAI_DUYET'] === 'REJECTED').length,
      total: filteredRequests.length
    };
  }, [filteredRequests]);

  const handleStatisticCardClick = (status) => {
    setFilterStatus(status);
    setSelectedRequests(new Set());
  };

  // ==================== APPROVAL FUNCTIONS ====================

  const handleOpenApprovalModal = (request, action) => {
    setSelectedRequest(request);
    setApprovalAction(action);
    setApprovalNote('');
    setRejectReason('');
    setShowApprovalModal(true);
  };

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    if (approvalAction === 'REJECT' && !rejectReason.trim()) {
      toast.error('Vui lòng nhập lý do từ chối');
      return;
    }

    try {
      setIsSubmitting(true);
      const currentTime = new Date().toISOString();
      const nguoiDuyet = currentUser ? (currentUser['Họ và Tên'] || currentUser.username) : '';

      const updatedRequest = {
        'REQUEST_ID': selectedRequest['REQUEST_ID'],
        'TRANG_THAI_DUYET': approvalAction === 'APPROVE' ? 'APPROVED' : 'REJECTED',
        'NGUOI_DUYET': nguoiDuyet,
        'THOI_GIAN_DUYET': currentTime,
        'LY_DO_TU_CHOI': approvalAction === 'REJECT' ? rejectReason : '',
        'GHI_CHU_DUYET': approvalNote
      };

      await authUtils.apiRequestKHO('XUATNHAPKHO_REQUEST', 'Edit', {
        "Rows": [updatedRequest]
      });

      if (approvalAction === 'APPROVE') {
        // APPROVE: Chỉ áp dụng cho ADD/DELETE (EDIT đã được áp dụng ngay)
        const loaiYeuCau = selectedRequest['LOAI_YEU_CAU'];
        if (loaiYeuCau === 'ADD' || loaiYeuCau === 'DELETE') {
          await applyRequestChanges(selectedRequest);
        }
      } else if (approvalAction === 'REJECT') {
        // REJECT: Xử lý revert dữ liệu (chỉ cho EDIT)
        const loaiYeuCau = selectedRequest['LOAI_YEU_CAU'];
        if (loaiYeuCau === 'EDIT') {
          await revertEditRequest(selectedRequest);
        }
      }

      toast.success(
        approvalAction === 'APPROVE'
          ? 'Đã duyệt yêu cầu thành công!'
          : 'Đã từ chối yêu cầu và revert về giá trị cũ!'
      );

      await fetchRequestList();
      setShowApprovalModal(false);
      setSelectedRequest(null);

    } catch (error) {
      console.error('Error approving request:', error);
      toast.error('Có lỗi xảy ra khi xử lý yêu cầu: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectRequest = (requestId) => {
    const newSelected = new Set(selectedRequests);
    if (newSelected.has(requestId)) {
      newSelected.delete(requestId);
    } else {
      newSelected.add(requestId);
    }
    setSelectedRequests(newSelected);
  };

  const handleSelectAll = () => {
    const pendingRequests = filteredRequests.filter(r => r['TRANG_THAI_DUYET'] === 'PENDING');
    if (selectedRequests.size === pendingRequests.length) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(pendingRequests.map(r => r['REQUEST_ID'])));
    }
  };

  const handleOpenBulkApprovalModal = (action) => {
    if (selectedRequests.size === 0) {
      toast.warning('Vui lòng chọn ít nhất một yêu cầu');
      return;
    }
    setBulkApprovalAction(action);
    setBulkApprovalNote('');
    setBulkRejectReason('');
    setShowBulkApprovalModal(true);
  };

  const handleBulkApproval = async () => {
    if (bulkApprovalAction === 'REJECT' && !bulkRejectReason.trim()) {
      toast.error('Vui lòng nhập lý do từ chối');
      return;
    }

    try {
      setIsSubmitting(true);
      const currentTime = new Date().toISOString();
      const nguoiDuyet = currentUser ? (currentUser['Họ và Tên'] || currentUser.username) : '';

      const selectedRequestsData = requestList.filter(r => selectedRequests.has(r['REQUEST_ID']));

      // STEP 1: Fetch phiếu + chi tiết MỘT LẦN (cần cho cả APPROVE và REJECT để tính header)
      // ✅ Tránh race condition: KHÔNG fetch lại sau khi ghi. Thay vào đó áp dụng
      //    thay đổi ADD/DELETE/EDIT vào bản sao trong bộ nhớ rồi tính tổng từ đó.
      toast.info(`Đang chuẩn bị dữ liệu...`);
      const [cachedPhieu, cachedChiTietBase] = await Promise.all([
        authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {})
      ]);
      // Bản sao chi tiết trong bộ nhớ để mô phỏng trạng thái SAU khi duyệt
      let simulatedChiTiet = [...cachedChiTietBase];

      // STEP 2: Chuẩn bị dữ liệu UPDATE trạng thái request
      const requestUpdates = selectedRequestsData.map(request => ({
        'REQUEST_ID': request['REQUEST_ID'],
        'TRANG_THAI_DUYET': bulkApprovalAction === 'APPROVE' ? 'APPROVED' : 'REJECTED',
        'NGUOI_DUYET': nguoiDuyet,
        'THOI_GIAN_DUYET': currentTime,
        'LY_DO_TU_CHOI': bulkApprovalAction === 'REJECT' ? bulkRejectReason : '',
        'GHI_CHU_DUYET': bulkApprovalNote
      }));

      // STEP 3: Update REQUEST status (1 API call)
      toast.info(`Đang cập nhật trạng thái yêu cầu...`);
      await authUtils.apiRequestKHO('XUATNHAPKHO_REQUEST', 'Edit', {
        "Rows": requestUpdates
      });

      const updatedSoPhieu = new Set(selectedRequestsData.map(r => r['SOPHIEU']));

      if (bulkApprovalAction === 'APPROVE') {
        // STEP 4a: Áp dụng ADD/DELETE vào chi tiết (EDIT đã apply ngay khi tạo request)
        const addRows = [];
        const deleteRows = [];

        for (const request of selectedRequestsData) {
          const loaiYeuCau = request['LOAI_YEU_CAU'];

          if (loaiYeuCau === 'ADD') {
            addRows.push({
              'ID_CT': request['ID_CT_NEW'],
              'SOPHIEU': request['SOPHIEU'],
              'NGHIEP_VU': request['NGHIEP_VU'],
              'KHO_XUAT': request['KHO_XUAT_NEW'],
              'KHO_NHAP': request['KHO_NHAP_NEW'],
              'NGAY_NHAP_XUAT': request['NGAY_NHAP_XUAT_NEW'],
              'NHOM_HANG': request['NHOM_HANG_NEW'],
              'MA_KIEN': request['MA_KIEN_NEW'],
              'DAY': request['DAY_NEW'],
              'RONG': request['RONG_NEW'],
              'DAI': request['DAI_NEW'],
              'THANH': request['THANH_NEW'],
              'SO_KHOI': request['SO_KHOI_NEW'],
              'TIEU_CHUAN': request['TIEU_CHUAN_NEW'],
              'DOI_HANG_KHO': request['DOI_HANG_KHO_NEW'],
              'DONGIA': request['DONGIA_NEW'],
              'THANHTIEN': request['THANHTIEN_NEW'],
              'GHICHU': request['GHICHU_NEW'] || ''
            });
          } else if (loaiYeuCau === 'DELETE') {
            deleteRows.push({ "ID_CT": request['ID_CT_OLD'] });
          }
        }

        if (addRows.length > 0) {
          toast.info(`Đang thêm ${addRows.length} chi tiết mới...`);
          await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Add', { "Rows": addRows });
          // Áp dụng vào bản sao bộ nhớ
          simulatedChiTiet = simulatedChiTiet.concat(addRows);
        }

        if (deleteRows.length > 0) {
          toast.info(`Đang xóa ${deleteRows.length} chi tiết...`);
          await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Delete', { "Rows": deleteRows });
          // Áp dụng vào bản sao bộ nhớ
          const deletedIds = new Set(deleteRows.map(r => String(r['ID_CT'])));
          simulatedChiTiet = simulatedChiTiet.filter(item => !deletedIds.has(String(item['ID_CT'])));
        }

      } else if (bulkApprovalAction === 'REJECT') {
        // STEP 4b: Revert EDIT về giá trị cũ (ADD/DELETE bị từ chối thì không cần làm gì)
        const revertRows = selectedRequestsData
          .filter(r => r['LOAI_YEU_CAU'] === 'EDIT')
          .map(request => ({
            'ID_CT': request['ID_CT_OLD'],
            'SOPHIEU': request['SOPHIEU'],
            'NGHIEP_VU': request['NGHIEP_VU'],
            'KHO_XUAT': request['KHO_XUAT_OLD'],
            'KHO_NHAP': request['KHO_NHAP_OLD'],
            'NGAY_NHAP_XUAT': request['NGAY_NHAP_XUAT_OLD'],
            'NHOM_HANG': request['NHOM_HANG_OLD'],
            'MA_KIEN': request['MA_KIEN_OLD'],
            'DAY': request['DAY_OLD'],
            'RONG': request['RONG_OLD'],
            'DAI': request['DAI_OLD'],
            'THANH': request['THANH_OLD'],
            'SO_KHOI': request['SO_KHOI_OLD'],
            'TIEU_CHUAN': request['TIEU_CHUAN_OLD'],
            'DOI_HANG_KHO': request['DOI_HANG_KHO_OLD'],
            'DONGIA': request['DONGIA_OLD'],
            'THANHTIEN': request['THANHTIEN_OLD'],
            'GHICHU': request['GHICHU_OLD'] || ''
          }));

        if (revertRows.length > 0) {
          toast.info(`Đang revert ${revertRows.length} thay đổi...`);
          await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Edit', { "Rows": revertRows });
          // Áp dụng vào bản sao bộ nhớ (thay dòng cũ bằng giá trị đã revert)
          const revertMap = new Map(revertRows.map(r => [String(r['ID_CT']), r]));
          simulatedChiTiet = simulatedChiTiet.map(item =>
            revertMap.has(String(item['ID_CT'])) ? revertMap.get(String(item['ID_CT'])) : item
          );
        }
      }

      // STEP 5: Tính header từ bản sao chi tiết trong bộ nhớ (KHÔNG fetch lại → tránh race condition)
      toast.info(`Đang cập nhật tổng cho ${updatedSoPhieu.size} phiếu...`);

      const headerUpdates = [];
      for (const soPhieu of updatedSoPhieu) {
        const chiTiet = simulatedChiTiet.filter(item => item['SOPHIEU'] === soPhieu);
        const currentPhieu = cachedPhieu.find(p => p['SOPHIEU'] === soPhieu);
        if (!currentPhieu) continue;

        const tongKhoiLuong = chiTiet.reduce((sum, item) => sum + (parseFloat(item['SO_KHOI']) || 0), 0);
        const thanhTien = chiTiet.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0);
        const ptVat = parseFloat(currentPhieu['PT VAT']) || 0;
        const tienVat = Math.round(thanhTien * (ptVat / 100));
        const tongTien = thanhTien + tienVat;

        headerUpdates.push({
          'SOPHIEU': soPhieu,
          'TONGKHOILUONG': tongKhoiLuong,
          'THANHTIEN': thanhTien,
          'TIEN VAT': tienVat,
          'TONGTIEN': tongTien
        });
      }

      if (headerUpdates.length > 0) {
        await authUtils.apiRequestKHO('XUATNHAPKHO', 'Edit', { "Rows": headerUpdates });
      }

      toast.success(
        bulkApprovalAction === 'APPROVE'
          ? `Đã duyệt ${selectedRequestsData.length} yêu cầu thành công!`
          : `Đã từ chối ${selectedRequestsData.length} yêu cầu!`
      );

      await fetchRequestList();
      setShowBulkApprovalModal(false);
      setSelectedRequests(new Set());

    } catch (error) {
      console.error('Error bulk approving:', error);
      toast.error('Có lỗi xảy ra khi xử lý hàng loạt: ' + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ Revert EDIT request (khi admin reject)
  const revertEditRequest = async (request) => {
    try {
      // ✅ Lấy dữ liệu CŨ từ request và update chi tiết về giá trị cũ
      const revertChiTiet = {
        'ID_CT': request['ID_CT_OLD'],
        'SOPHIEU': request['SOPHIEU'],
        'NGHIEP_VU': request['NGHIEP_VU'],
        'KHO_XUAT': request['KHO_XUAT_OLD'],
        'KHO_NHAP': request['KHO_NHAP_OLD'],
        'NGAY_NHAP_XUAT': request['NGAY_NHAP_XUAT_OLD'],
        'NHOM_HANG': request['NHOM_HANG_OLD'],
        'MA_KIEN': request['MA_KIEN_OLD'],
        'DAY': request['DAY_OLD'],
        'RONG': request['RONG_OLD'],
        'DAI': request['DAI_OLD'],
        'THANH': request['THANH_OLD'],
        'SO_KHOI': request['SO_KHOI_OLD'],
        'TIEU_CHUAN': request['TIEU_CHUAN_OLD'],
        'DOI_HANG_KHO': request['DOI_HANG_KHO_OLD'],
        'DONGIA': request['DONGIA_OLD'],
        'THANHTIEN': request['THANHTIEN_OLD'],
        'GHICHU': request['GHICHU_OLD']
      };

      await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Edit', {
        "Rows": [revertChiTiet]
      });

      // ✅ Cập nhật lại header phiếu
      const [chiTietResponse, phieuResponse] = await Promise.all([
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {})
      ]);

      const soPhieu = request['SOPHIEU'];
      // ✅ Mô phỏng revert vào bộ nhớ để tránh race (nếu fetch chưa phản ánh giá trị đã revert)
      const chiTietReverted = chiTietResponse.map(item =>
        String(item['ID_CT']) === String(request['ID_CT_OLD'])
          ? { ...item, 'SO_KHOI': request['SO_KHOI_OLD'], 'THANHTIEN': request['THANHTIEN_OLD'] }
          : item
      );
      const chiTiet = chiTietReverted.filter(item => item['SOPHIEU'] === soPhieu);
      const currentPhieu = phieuResponse.find(p => p['SOPHIEU'] === soPhieu);

      if (currentPhieu) {
        const tongKhoiLuong = chiTiet.reduce((sum, item) => sum + (parseFloat(item['SO_KHOI']) || 0), 0);
        const thanhTien = chiTiet.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0);
        const ptVat = parseFloat(currentPhieu['PT VAT']) || 0;
        const tienVat = Math.round(thanhTien * (ptVat / 100));
        const tongTien = thanhTien + tienVat;

        await authUtils.apiRequestKHO('XUATNHAPKHO', 'Edit', {
          "Rows": [{
            'SOPHIEU': soPhieu,
            'TONGKHOILUONG': tongKhoiLuong,
            'THANHTIEN': thanhTien,
            'TIEN VAT': tienVat,
            'TONGTIEN': tongTien
          }]
        });
      }

      console.log(`✅ Reverted EDIT request ${request['REQUEST_ID']} to original values`);
    } catch (error) {
      console.error('Error reverting edit request:', error);
      throw error;
    }
  };

  // ✅ applyRequestChanges nhận thêm cache để không fetch lại bảng
  const applyRequestChanges = async (request, cachedChiTiet = null, cachedPhieu = null) => {
    const loaiYeuCau = request['LOAI_YEU_CAU'];

    if (loaiYeuCau === 'EDIT') {
      // ✅ PHẢI dùng ID_CT_OLD để cập nhật dòng cũ với giá trị mới
      const updatedChiTiet = {
        'ID_CT': request['ID_CT_OLD'],
        'SOPHIEU': request['SOPHIEU'],
        'NGHIEP_VU': request['NGHIEP_VU'],
        'KHO_XUAT': request['KHO_XUAT_NEW'],
        'KHO_NHAP': request['KHO_NHAP_NEW'],
        'NGAY_NHAP_XUAT': request['NGAY_NHAP_XUAT_NEW'],
        'NHOM_HANG': request['NHOM_HANG_NEW'],
        'MA_KIEN': request['MA_KIEN_NEW'],
        'DAY': request['DAY_NEW'],
        'RONG': request['RONG_NEW'],
        'DAI': request['DAI_NEW'],
        'THANH': request['THANH_NEW'],
        'SO_KHOI': request['SO_KHOI_NEW'],
        'TIEU_CHUAN': request['TIEU_CHUAN_NEW'],
        'DOI_HANG_KHO': request['DOI_HANG_KHO_NEW'],
        'DONGIA': request['DONGIA_NEW'],
        'THANHTIEN': request['THANHTIEN_NEW'],
        'GHICHU': request['GHICHU_NEW'] || ''
      };

      await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Edit', {
        "Rows": [updatedChiTiet]
      });

    } else if (loaiYeuCau === 'ADD') {
      const newChiTiet = {
        'ID_CT': request['ID_CT_NEW'],
        'SOPHIEU': request['SOPHIEU'],
        'NGHIEP_VU': request['NGHIEP_VU'],
        'KHO_XUAT': request['KHO_XUAT_NEW'],
        'KHO_NHAP': request['KHO_NHAP_NEW'],
        'NGAY_NHAP_XUAT': request['NGAY_NHAP_XUAT_NEW'],
        'NHOM_HANG': request['NHOM_HANG_NEW'],
        'MA_KIEN': request['MA_KIEN_NEW'],
        'DAY': request['DAY_NEW'],
        'RONG': request['RONG_NEW'],
        'DAI': request['DAI_NEW'],
        'THANH': request['THANH_NEW'],
        'SO_KHOI': request['SO_KHOI_NEW'],
        'TIEU_CHUAN': request['TIEU_CHUAN_NEW'],
        'DOI_HANG_KHO': request['DOI_HANG_KHO_NEW'],
        'DONGIA': request['DONGIA_NEW'],
        'THANHTIEN': request['THANHTIEN_NEW'],
        'GHICHU': request['GHICHU_NEW'] || ''
      };

      await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Add', {
        "Rows": [newChiTiet]
      });

    } else if (loaiYeuCau === 'DELETE') {
      await authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Delete', {
        "Rows": [{ "ID_CT": request['ID_CT_OLD'] }]
      });
    }

    // Khi gọi đơn lẻ (duyệt 1 cái) thì vẫn gọi updatePhieuTotals bình thường
    // Khi duyệt nhiều thì skip ở đây, sẽ gọi tập trung sau ở handleBulkApproval
    if (!cachedChiTiet && !cachedPhieu) {
      await updatePhieuTotals(request['SOPHIEU'], request);
    }
  };

  // ✅ Mô phỏng trạng thái chi tiết SAU khi áp dụng 1 request (ADD/DELETE/EDIT) vào bản sao bộ nhớ.
  //    Tránh race condition do fetch lại ngay sau khi ghi (AppSheet eventual-consistency).
  const applyRequestToChiTiet = (chiTietArr, request) => {
    const loai = request['LOAI_YEU_CAU'];
    if (loai === 'ADD') {
      // Idempotent: chỉ thêm nếu fetch chưa có dòng này (tránh cộng đôi khi AppSheet đã kịp cập nhật)
      const daCo = chiTietArr.some(item => String(item['ID_CT']) === String(request['ID_CT_NEW']));
      if (daCo) return chiTietArr;
      return chiTietArr.concat([{
        'ID_CT': request['ID_CT_NEW'],
        'SOPHIEU': request['SOPHIEU'],
        'SO_KHOI': request['SO_KHOI_NEW'],
        'THANHTIEN': request['THANHTIEN_NEW']
      }]);
    }
    if (loai === 'DELETE') {
      return chiTietArr.filter(item => String(item['ID_CT']) !== String(request['ID_CT_OLD']));
    }
    if (loai === 'EDIT') {
      return chiTietArr.map(item =>
        String(item['ID_CT']) === String(request['ID_CT_OLD'])
          ? { ...item, 'SO_KHOI': request['SO_KHOI_NEW'], 'THANHTIEN': request['THANHTIEN_NEW'] }
          : item
      );
    }
    return chiTietArr;
  };

  const updatePhieuTotals = async (soPhieu, appliedRequest = null) => {
    try {
      // Dùng cho duyệt đơn lẻ: fetch bình thường
      const [chiTietResponse, phieuResponse] = await Promise.all([
        authUtils.apiRequestKHO('XUATNHAPKHO_CHITIET', 'Find', {}),
        authUtils.apiRequestKHO('XUATNHAPKHO', 'Find', {})
      ]);
      // ✅ Nếu fetch chưa phản ánh thay đổi vừa ghi (race), mô phỏng thay đổi vào bộ nhớ
      const chiTietData = appliedRequest
        ? applyRequestToChiTiet(chiTietResponse, appliedRequest)
        : chiTietResponse;
      await _calcAndSavePhieuTotals(soPhieu, chiTietData, phieuResponse);
    } catch (error) {
      console.error('Error updating phieu totals:', error);
    }
  };

  const _calcAndSavePhieuTotals = async (soPhieu, chiTietData, phieuData) => {
    const chiTiet = chiTietData.filter(item => item['SOPHIEU'] === soPhieu);
    const currentPhieu = phieuData.find(p => p['SOPHIEU'] === soPhieu);
    if (!currentPhieu) return;

    const tongKhoiLuong = chiTiet.reduce((sum, item) => sum + (parseFloat(item['SO_KHOI']) || 0), 0);
    const thanhTien = chiTiet.reduce((sum, item) => sum + (parseFloat(item['THANHTIEN']) || 0), 0);
    const ptVat = parseFloat(currentPhieu['PT VAT']) || 0;
    const tienVat = Math.round(thanhTien * (ptVat / 100));
    const tongTien = thanhTien + tienVat;

    await authUtils.apiRequestKHO('XUATNHAPKHO', 'Edit', {
      "Rows": [{
        'SOPHIEU': soPhieu,
        'TONGKHOILUONG': tongKhoiLuong,
        'THANHTIEN': thanhTien,
        'TIEN VAT': tienVat,
        'TONGTIEN': tongTien
      }]
    });

    console.log(`✅ Đã cập nhật tổng phiếu ${soPhieu}: Thành tiền=${thanhTien}, VAT=${tienVat}, Tổng=${tongTien}`);
  };

  // ==================== UI FUNCTIONS ====================

  const toggleRowExpansion = (requestId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedRows(newExpanded);
  };

  const handleRefresh = async () => {
    toast.info('Đang tải lại dữ liệu...');
    await fetchRequestList();
    setSelectedRequests(new Set());
    toast.success('Đã tải lại dữ liệu thành công!');
  };

  const pendingRequestsCount = filteredRequests.filter(r => r['TRANG_THAI_DUYET'] === 'PENDING').length;

  return (
    <div className="p-3 md:p-4 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      <div className="mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-3 md:p-4 mb-4 border border-gray-100">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-3 gap-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-md">
                <CheckCircle className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-800">Duyệt Yêu Cầu Thay Đổi</h1>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Quản lý và duyệt các yêu cầu thay đổi phiếu</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedRequests.size > 0 && (
                <>
                  <button
                    onClick={() => handleOpenBulkApprovalModal('APPROVE')}
                    className="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm font-medium"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Duyệt ({selectedRequests.size})
                  </button>
                  <button
                    onClick={() => handleOpenBulkApprovalModal('REJECT')}
                    className="px-3 py-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Từ chối ({selectedRequests.size})
                  </button>
                </>
              )}
              <button
                onClick={handleRefresh}
                className="px-3 py-1.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm"
                title="Tải lại dữ liệu"
              >
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Làm mới</span>
              </button>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-all shadow-sm hover:shadow text-sm ${showFilters
                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                  : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                  }`}
              >
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">Bộ lọc</span>
              </button>
            </div>
          </div>

          {/* ✅ CẬP NHẬT: Statistics Cards - Tổng yêu cầu lên đầu */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3 mb-3">
            <button
              onClick={() => handleStatisticCardClick('ALL')}
              className={`text-left bg-gradient-to-br from-blue-50 to-blue-100 p-3 rounded-lg border-l-4 border-blue-500 shadow-sm hover:shadow-md transition-all ${filterStatus === 'ALL' ? 'ring-2 ring-blue-500' : ''
                }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <FileText className="w-4 h-4 text-blue-600" />
                <p className="text-xs font-medium text-blue-700">Tổng yêu cầu</p>
              </div>
              <p className="text-2xl font-bold text-blue-900">{statistics.total}</p>
            </button>

            <button
              onClick={() => handleStatisticCardClick('PENDING')}
              className={`text-left bg-gradient-to-br from-yellow-50 to-yellow-100 p-3 rounded-lg border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-all ${filterStatus === 'PENDING' ? 'ring-2 ring-yellow-500' : ''
                }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-yellow-600" />
                <p className="text-xs font-medium text-yellow-700">Chờ duyệt</p>
              </div>
              <p className="text-2xl font-bold text-yellow-900">{statistics.pending}</p>
            </button>

            <button
              onClick={() => handleStatisticCardClick('APPROVED')}
              className={`text-left bg-gradient-to-br from-green-50 to-green-100 p-3 rounded-lg border-l-4 border-green-500 shadow-sm hover:shadow-md transition-all ${filterStatus === 'APPROVED' ? 'ring-2 ring-green-500' : ''
                }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <p className="text-xs font-medium text-green-700">Đã duyệt</p>
              </div>
              <p className="text-2xl font-bold text-green-900">{statistics.approved}</p>
            </button>

            <button
              onClick={() => handleStatisticCardClick('REJECTED')}
              className={`text-left bg-gradient-to-br from-red-50 to-red-100 p-3 rounded-lg border-l-4 border-red-500 shadow-sm hover:shadow-md transition-all ${filterStatus === 'REJECTED' ? 'ring-2 ring-red-500' : ''
                }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <XCircle className="w-4 h-4 text-red-600" />
                <p className="text-xs font-medium text-red-700">Từ chối</p>
              </div>
              <p className="text-2xl font-bold text-red-900">{statistics.rejected}</p>
            </button>
          </div>

          {/* Filters Section */}
          {showFilters && (
            <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 mb-3 animate-fadeIn">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Trạng thái</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="ALL">Tất cả</option>
                    <option value="PENDING">Chờ duyệt</option>
                    <option value="APPROVED">Đã duyệt</option>
                    <option value="REJECTED">Từ chối</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Loại yêu cầu</label>
                  <select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="ALL">Tất cả</option>
                    <option value="ADD">Thêm mới</option>
                    <option value="EDIT">Sửa đổi</option>
                    <option value="DELETE">Xóa</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Từ ngày</label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Đến ngày</label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex justify-end mt-2">
                <button
                  onClick={() => {
                    setFilterStatus('ALL');
                    setFilterType('ALL');
                    setFilterDateFrom('');
                    setFilterDateTo('');
                  }}
                  className="px-3 py-1.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Xóa bộ lọc
                </button>
              </div>
            </div>
          )}

          {/* Search Bar */}
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Tìm kiếm theo mã yêu cầu, số phiếu, mã kiện, người yêu cầu..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="relative">
                  <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-500 rounded-full animate-spin"></div>
                  <div className="absolute top-0 left-0 w-12 h-12 border-4 border-transparent border-t-blue-300 rounded-full animate-pulse"></div>
                </div>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">Không tìm thấy yêu cầu nào</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
                  <tr>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">
                      <input
                        type="checkbox"
                        checked={selectedRequests.size === pendingRequestsCount && pendingRequestsCount > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Thời gian</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Số phiếu</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Mã kiện</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Người yêu cầu</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Loại</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-blue-900">Trạng thái</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">Thao tác</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-blue-900">Chi tiết</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredRequests.map((request, index) => (
                    <React.Fragment key={index}>
                      <tr className="hover:bg-blue-50 transition-colors">
                        <td className="px-3 py-2 text-center">
                          {request['TRANG_THAI_DUYET'] === 'PENDING' && (
                            <input
                              type="checkbox"
                              checked={selectedRequests.has(request['REQUEST_ID'])}
                              onChange={() => handleSelectRequest(request['REQUEST_ID'])}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                            />
                          )}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {formatDate(request['THOI_GIAN_YEU_CAU'])}
                        </td>
                        <td className="px-3 py-2 text-xs font-medium text-gray-900">
                          {request['SOPHIEU']}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {request['MA_KIEN_NEW'] || request['MA_KIEN_OLD'] || '-'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-700">
                          {request['NGUOI_YEU_CAU']}
                        </td>
                        <td className="px-3 py-2">
                          {getTypeBadge(request['LOAI_YEU_CAU'])}
                        </td>
                        <td className="px-3 py-2">
                          {getStatusBadge(request['TRANG_THAI_DUYET'])}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center justify-center gap-1">
                            {request['TRANG_THAI_DUYET'] === 'PENDING' && (
                              <>
                                <button
                                  onClick={() => handleOpenApprovalModal(request, 'APPROVE')}
                                  className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                  title="Duyệt"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleOpenApprovalModal(request, 'REJECT')}
                                  className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Từ chối"
                                >
                                  <XCircle className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {request['TRANG_THAI_DUYET'] !== 'PENDING' && (
                              <span className="text-xs text-gray-500 italic">
                                {request['TRANG_THAI_DUYET'] === 'APPROVED' ? 'Đã duyệt' : 'Đã từ chối'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => toggleRowExpansion(request['REQUEST_ID'])}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title={expandedRows.has(request['REQUEST_ID']) ? 'Thu gọn' : 'Xem chi tiết'}
                          >
                            {expandedRows.has(request['REQUEST_ID']) ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                      </tr>

                      {/* ✅ CẬP NHẬT: Expanded Row - Highlight inline, gọn hơn */}
                      {expandedRows.has(request['REQUEST_ID']) && (
                        <tr>
                          <td colSpan="9" className="px-3 py-3 bg-gray-50">
                            <div className="space-y-3">
                              {/* Lý do yêu cầu */}
                              {request['LY_DO_YEU_CAU'] && (
                                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                                  <h4 className="font-semibold text-yellow-900 mb-1 text-xs">Lý do yêu cầu: <span className="text-xs text-gray-700">{request['LY_DO_YEU_CAU']}</span></h4>

                                </div>
                              )}

                              {/* Chi tiết thay đổi */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {/* Dữ liệu cũ */}
                                {request['LOAI_YEU_CAU'] !== 'ADD' && (
                                  <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                                    <h4 className="font-medium text-red-900 mb-2 text-xs flex items-center gap-1">
                                      <XCircle className="w-3 h-3" />
                                      Dữ liệu cũ
                                    </h4>
                                    <div className="space-y-1.5">
                                      <div className="text-xs"><strong>Mã kiện:</strong> {request['MA_KIEN_OLD']}</div>
                                      <div className="text-xs"><strong>Nhóm hàng:</strong> {request['NHOM_HANG_OLD']}</div>
                                      {/* <div className="text-xs"><strong>Kích thước:</strong> {request['DAY_OLD']}x{request['RONG_OLD']}x{request['DAI_OLD']}</div> */}
                                      <div className="text-xs"><strong>Số thanh:</strong> {request['THANH_OLD']}</div>
                                      <div className="text-xs"><strong>Số khối:</strong> {formatNumber(request['SO_KHOI_OLD'], 4)} m³</div>
                                      <div className="text-xs"><strong>Tiêu chuẩn:</strong> {request['TIEU_CHUAN_OLD']}</div>
                                      <div className="text-xs"><strong>Đội hàng khô:</strong> {request['DOI_HANG_KHO_OLD'] || '-'}</div>
                                      {request['DONGIA_OLD'] > 0 && (
                                        <>
                                          <div className="text-xs"><strong>Đơn giá:</strong> {formatCurrency(request['DONGIA_OLD'])}</div>
                                          <div className="text-xs"><strong>Thành tiền:</strong> {formatCurrency(request['THANHTIEN_OLD'])}</div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* ✅ Dữ liệu mới với highlight inline */}
                                {request['LOAI_YEU_CAU'] !== 'DELETE' && (
                                  <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                                    <h4 className="font-medium text-green-900 mb-2 text-xs flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3" />
                                      Dữ liệu mới
                                    </h4>
                                    <div className="space-y-1.5">
                                      {request['LOAI_YEU_CAU'] === 'EDIT' ? (
                                        <>
                                          {renderFieldWithHighlight('Mã kiện', request['MA_KIEN_OLD'], request['MA_KIEN_NEW'])}
                                          {renderFieldWithHighlight('Nhóm hàng', request['NHOM_HANG_OLD'], request['NHOM_HANG_NEW'])}
                                          {/* {renderFieldWithHighlight('Dày', request['DAY_OLD'], request['DAY_NEW'])}
                                          {renderFieldWithHighlight('Rộng', request['RONG_OLD'], request['RONG_NEW'])}
                                          {renderFieldWithHighlight('Dài', request['DAI_OLD'], request['DAI_NEW'])} */}
                                          {renderFieldWithHighlight('Số thanh', request['THANH_OLD'], request['THANH_NEW'])}
                                          {renderFieldWithHighlight('Số khối', formatNumber(request['SO_KHOI_OLD'], 4), formatNumber(request['SO_KHOI_NEW'], 4))}
                                          {renderFieldWithHighlight('Tiêu chuẩn', request['TIEU_CHUAN_OLD'], request['TIEU_CHUAN_NEW'])}
                                          {renderFieldWithHighlight('Đội hàng khô', request['DOI_HANG_KHO_OLD'] || '-', request['DOI_HANG_KHO_NEW'] || '-')}
                                          {request['DONGIA_NEW'] > 0 && (
                                            <>
                                              {renderFieldWithHighlight('Đơn giá', formatCurrency(request['DONGIA_OLD']), formatCurrency(request['DONGIA_NEW']))}
                                              {renderFieldWithHighlight('Thành tiền', formatCurrency(request['THANHTIEN_OLD']), formatCurrency(request['THANHTIEN_NEW']))}
                                            </>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className="text-xs"><strong>Mã kiện:</strong> {request['MA_KIEN_NEW']}</div>
                                          <div className="text-xs"><strong>Nhóm hàng:</strong> {request['NHOM_HANG_NEW']}</div>
                                          <div className="text-xs"><strong>Kích thước:</strong> {request['DAY_NEW']}x{request['RONG_NEW']}x{request['DAI_NEW']}</div>
                                          <div className="text-xs"><strong>Số thanh:</strong> {request['THANH_NEW']}</div>
                                          <div className="text-xs"><strong>Số khối:</strong> {formatNumber(request['SO_KHOI_NEW'], 4)} m³</div>
                                          <div className="text-xs"><strong>Tiêu chuẩn:</strong> {request['TIEU_CHUAN_NEW']}</div>
                                          <div className="text-xs"><strong>Đội hàng khô:</strong> {request['DOI_HANG_KHO_NEW'] || '-'}</div>
                                          {request['DONGIA_NEW'] > 0 && (
                                            <>
                                              <div className="text-xs"><strong>Đơn giá:</strong> {formatCurrency(request['DONGIA_NEW'])}</div>
                                              <div className="text-xs"><strong>Thành tiền:</strong> {formatCurrency(request['THANHTIEN_NEW'])}</div>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>

                              {/* Thông tin duyệt */}
                              {request['TRANG_THAI_DUYET'] !== 'PENDING' && (
                                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                  <h4 className="font-semibold text-purple-900 mb-2 text-xs">Thông tin duyệt:</h4>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div><strong>Người duyệt:</strong> {request['NGUOI_DUYET']}</div>
                                    <div><strong>Thời gian:</strong> {formatDate(request['THOI_GIAN_DUYET'])}</div>
                                    {request['LY_DO_TU_CHOI'] && (
                                      <div className="col-span-2">
                                        <strong>Lý do từ chối:</strong> {request['LY_DO_TU_CHOI']}
                                      </div>
                                    )}
                                    {request['GHI_CHU_DUYET'] && (
                                      <div className="col-span-2">
                                        <strong>Ghi chú:</strong> {request['GHI_CHU_DUYET']}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Modal Xác Nhận Duyệt/Từ Chối - Giữ nguyên */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className={`flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4 ${approvalAction === 'APPROVE' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                {approvalAction === 'APPROVE' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                {approvalAction === 'APPROVE' ? 'Xác nhận duyệt yêu cầu' : 'Xác nhận từ chối yêu cầu'}
              </h3>
              <p className="text-sm text-gray-600 text-center">
                Mã yêu cầu: <strong>{selectedRequest['REQUEST_ID']}</strong>
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Thông tin yêu cầu */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-600">Số phiếu:</span>
                    <span className="ml-2 font-medium">{selectedRequest['SOPHIEU']}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Người yêu cầu:</span>
                    <span className="ml-2 font-medium">{selectedRequest['NGUOI_YEU_CAU']}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Loại:</span>
                    <span className="ml-2">{getTypeBadge(selectedRequest['LOAI_YEU_CAU'])}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Mã kiện:</span>
                    <span className="ml-2 font-medium">
                      {selectedRequest['MA_KIEN_NEW'] || selectedRequest['MA_KIEN_OLD']}
                    </span>
                  </div>
                </div>
              </div>

              {/* Lý do yêu cầu */}
              {selectedRequest['LY_DO_YEU_CAU'] && (
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 mb-4">
                  <h4 className="font-semibold text-yellow-900 mb-2 text-sm">Lý do yêu cầu:  <span className="text-sm text-gray-700">{selectedRequest['LY_DO_YEU_CAU']}</span></h4>

                </div>
              )}

              {/* Form nhập lý do từ chối */}
              {approvalAction === 'REJECT' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lý do từ chối <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder="Nhập lý do từ chối yêu cầu này..."
                  />
                </div>
              )}

              {/* Form nhập ghi chú */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú {approvalAction === 'APPROVE' && '(không bắt buộc)'}
                </label>
                <textarea
                  value={approvalNote}
                  onChange={(e) => setApprovalNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Nhập ghi chú thêm..."
                />
              </div>

              {/* Cảnh báo */}
              <div className={`p-4 rounded-lg border ${approvalAction === 'APPROVE'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
                }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${approvalAction === 'APPROVE' ? 'text-green-600' : 'text-red-600'
                    }`} />
                  <div className={`text-sm ${approvalAction === 'APPROVE' ? 'text-green-800' : 'text-red-800'
                    }`}>
                    <p className="font-semibold mb-1">
                      {approvalAction === 'APPROVE' ? 'Lưu ý khi duyệt:' : 'Lưu ý khi từ chối:'}
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      {approvalAction === 'APPROVE' ? (
                        <>
                          <li>Thay đổi sẽ được áp dụng ngay vào database</li>
                          <li>Tổng khối lượng và tổng tiền của phiếu sẽ được cập nhật tự động</li>
                          <li>Hành động này không thể hoàn tác</li>
                        </>
                      ) : (
                        <>
                          <li>Yêu cầu sẽ bị từ chối và không được áp dụng</li>
                          <li>Người yêu cầu sẽ nhận được thông báo từ chối</li>
                          <li>Vui lòng nhập rõ lý do để người yêu cầu hiểu</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setSelectedRequest(null);
                  setApprovalNote('');
                  setRejectReason('');
                }}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Hủy
              </button>
              <button
                onClick={handleApproveRequest}
                disabled={isSubmitting || (approvalAction === 'REJECT' && !rejectReason.trim())}
                className={`flex-1 px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 ${approvalAction === 'APPROVE'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                  : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang xử lý...
                  </>
                ) : (
                  <>
                    {approvalAction === 'APPROVE' ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Duyệt yêu cầu
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4" />
                        Từ chối yêu cầu
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ THÊM: Modal Bulk Approval */}
      {showBulkApprovalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className={`flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4 ${bulkApprovalAction === 'APPROVE' ? 'bg-green-100' : 'bg-red-100'
                }`}>
                {bulkApprovalAction === 'APPROVE' ? (
                  <CheckCircle className="w-6 h-6 text-green-600" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-600" />
                )}
              </div>
              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                {bulkApprovalAction === 'APPROVE'
                  ? `Xác nhận duyệt ${selectedRequests.size} yêu cầu`
                  : `Xác nhận từ chối ${selectedRequests.size} yêu cầu`}
              </h3>
              <p className="text-sm text-gray-600 text-center">
                Bạn đang thực hiện hàng loạt cho <strong>{selectedRequests.size}</strong> yêu cầu
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Danh sách yêu cầu được chọn */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-4">
                <h4 className="font-semibold text-gray-900 mb-3 text-sm">
                  Danh sách yêu cầu ({selectedRequests.size}):
                </h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {requestList
                    .filter(r => selectedRequests.has(r['REQUEST_ID']))
                    .map((request, index) => (
                      <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200 text-xs">
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{request['SOPHIEU']}</div>
                          <div className="text-gray-600">
                            {request['MA_KIEN_NEW'] || request['MA_KIEN_OLD']} - {request['NGUOI_YEU_CAU']}
                          </div>
                        </div>
                        {getTypeBadge(request['LOAI_YEU_CAU'])}
                      </div>
                    ))}
                </div>
              </div>

              {/* Form nhập lý do từ chối */}
              {bulkApprovalAction === 'REJECT' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Lý do từ chối <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={bulkRejectReason}
                    onChange={(e) => setBulkRejectReason(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                    placeholder="Nhập lý do từ chối chung cho tất cả yêu cầu..."
                  />
                </div>
              )}

              {/* Form nhập ghi chú */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú chung {bulkApprovalAction === 'APPROVE' && '(không bắt buộc)'}
                </label>
                <textarea
                  value={bulkApprovalNote}
                  onChange={(e) => setBulkApprovalNote(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Nhập ghi chú chung cho tất cả yêu cầu..."
                />
              </div>

              {/* Cảnh báo */}
              <div className={`p-4 rounded-lg border ${bulkApprovalAction === 'APPROVE'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200'
                }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${bulkApprovalAction === 'APPROVE' ? 'text-green-600' : 'text-red-600'
                    }`} />
                  <div className={`text-sm ${bulkApprovalAction === 'APPROVE' ? 'text-green-800' : 'text-red-800'
                    }`}>
                    <p className="font-semibold mb-1">
                      {bulkApprovalAction === 'APPROVE' ? 'Lưu ý khi duyệt hàng loạt:' : 'Lưu ý khi từ chối hàng loạt:'}
                    </p>
                    <ul className="list-disc list-inside space-y-1">
                      {bulkApprovalAction === 'APPROVE' ? (
                        <>
                          <li>Tất cả {selectedRequests.size} yêu cầu sẽ được duyệt cùng lúc</li>
                          <li>Thay đổi sẽ được áp dụng ngay vào database</li>
                          <li>Tổng khối lượng và tổng tiền của các phiếu sẽ được cập nhật tự động</li>
                          <li>Hành động này không thể hoàn tác</li>
                        </>
                      ) : (
                        <>
                          <li>Tất cả {selectedRequests.size} yêu cầu sẽ bị từ chối</li>
                          <li>Người yêu cầu sẽ nhận được thông báo từ chối</li>
                          <li>Lý do từ chối sẽ áp dụng chung cho tất cả yêu cầu</li>
                          <li>Vui lòng nhập rõ lý do để người yêu cầu hiểu</li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  setShowBulkApprovalModal(false);
                  setBulkApprovalNote('');
                  setBulkRejectReason('');
                }}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Hủy
              </button>
              <button
                onClick={handleBulkApproval}
                disabled={isSubmitting || (bulkApprovalAction === 'REJECT' && !bulkRejectReason.trim())}
                className={`flex-1 px-4 py-2 text-sm text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2 ${bulkApprovalAction === 'APPROVE'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'
                  : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                  }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Đang xử lý...
                  </>
                ) : (
                  <>
                    {bulkApprovalAction === 'APPROVE' ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Duyệt {selectedRequests.size} yêu cầu
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4" />
                        Từ chối {selectedRequests.size} yêu cầu
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ApprovalManagement;
